
import React, { memo, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";
import { useScreenCapture } from "../../hooks/use-screen-capture";
import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";
import { triggerNavigationBack } from '../specialty-selector/navigation-events';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faMicrophone, 
  faMicrophoneSlash, 
  faVideo, 
  faShareSquare, 
  faPhone, 
  faPhoneSlash 
} from "@fortawesome/free-solid-svg-icons";
import { getRemainingCases, DAILY_FREE_LIMIT } from '../../utils/caseTracker';

export type ControlTrayProps = {
  videoRef: RefObject<HTMLVideoElement>;
  children?: ReactNode;
  supportsVideo: boolean;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
};

const MediaButton = ({
  icon,
  active = true,
  onClick,
  danger = false,
  className,
  speaking = false,
}: {
  icon: any;
  active?: boolean;
  onClick: () => void;
  danger?: boolean;
  className?: string;
  speaking?: boolean;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "control-btn",
      {
        "btn-call": danger,
        "btn-mic": !danger,
        "btn-active": active,
        "btn-inactive": !active,
        "speaking-animation": speaking,
      },
      className
    )}
  >
    <FontAwesomeIcon icon={icon} className="icon" />
  </button>
);

function ControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => {},
  supportsVideo,
}: ControlTrayProps) {
  const videoStreams = [useWebcam(), useScreenCapture()];
  const [activeVideoStream, setActiveVideoStream] =
    useState<MediaStream | null>(null);
  const [webcam, screenCapture] = videoStreams;
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const renderCanvasRef = useRef<HTMLCanvasElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);

  const { client, connected, connect, disconnect, volume } =
    useLiveAPIContext();

  const [isMicOn, setIsMicOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`,
    );
  }, [inVolume]);

  useEffect(() => {
    const onData = (base64: string) => {
      client.sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };
    if (connected && !muted && audioRecorder) {
      audioRecorder.on("data", onData).on("volume", setInVolume).start();
    } else {
      audioRecorder.stop();
    }
    return () => {
      audioRecorder.off("data", onData).off("volume", setInVolume);
    };
  }, [connected, client, muted, audioRecorder]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = activeVideoStream;
    }

    let timeoutId = -1;

    function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d")!;
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;
      if (canvas.width + canvas.height > 0) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 1.0);
        const data = base64.slice(base64.indexOf(",") + 1, Infinity);
        client.sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
      }
      if (connected) {
        timeoutId = window.setTimeout(sendVideoFrame, 2000);
      }
    }
    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, client, videoRef]);

  //handler for swapping from one video-stream to the next
  const changeStreams = (next?: UseMediaStreamResult) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
  };

  const handleConnect = async () => {
    try {
      if (connected) {
        await disconnect();
        triggerNavigationBack();
      } else {
        await connect();
      }
    } catch (error) {
      console.error('Connection failed:', error);
    }
  };

  const handleMicToggle = () => {
    setMuted((prev) => !prev);
    setIsMicOn((prev) => !prev);
  };

  return (
    <section className="control-tray">
      {/*<div className="free-cases-info">
        // <span>{getRemainingCases()} of {DAILY_FREE_LIMIT} free cases remaining today</span>
      // </div>*/}

      <nav className={cn("actions-nav", { connected })}>
        <MediaButton
          icon={isMicOn ? faMicrophone : faMicrophoneSlash}
          active={connected && isMicOn}
          speaking={inVolume > 0.1} // Example threshold
          onClick={handleMicToggle}
        />
        <MediaButton
          icon={faVideo}
          active={false}
          onClick={() => setIsVideoOn(!isVideoOn)}
        />
        <div className="audio-pulse-container">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>

        {supportsVideo && (
          <>
            <MediaButton
              icon={faShareSquare}
              active={false}
              onClick={() => {}}
            />
            <MediaButton
              icon={connected ? faPhoneSlash : faPhone}
              danger={connected}
              active={connected}
              onClick={handleConnect}
            />
          </>
        )}
        {children}
      </nav>
    </section>
  );
}

export default memo(ControlTray);